import { useState } from "react";
import api from "@/lib/api";
import { GitCompareArrows, TriangleAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EXAMPLES = [
  ["Apple", "Samsung"],
  ["MTN Nigeria", "Airtel Africa"],
  ["Dangote Cement", "BUA Cement"],
  ["Shell", "ExxonMobil"],
  ["Shopify", "Amazon"],
];

export default function Compare() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (ca = a, cb = b) => {
    if (!ca || !cb) return;
    setA(ca); setB(cb); setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/compare", { company_a: ca, company_b: cb });
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Comparison failed."); }
    finally { setLoading(false); }
  };

  const winnerCls = (w, side) => (w === side ? "text-emerald-700 font-semibold" : "text-stone-700");

  return (
    <main className="mx-auto max-w-5xl px-6 py-10 md:px-8">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500"><GitCompareArrows size={14} strokeWidth={1.5} /> Compare</p>
      <h1 className="mt-2 font-head text-3xl font-medium tracking-tight text-stone-900 sm:text-4xl">Compare companies across countries</h1>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
        <input data-testid="compare-a" value={a} onChange={(e) => setA(e.target.value)} placeholder="First company"
          className="h-12 w-full rounded-lg border border-stone-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20" />
        <span className="text-center font-head text-sm font-medium text-stone-400">vs</span>
        <input data-testid="compare-b" value={b} onChange={(e) => setB(e.target.value)} placeholder="Second company"
          className="h-12 w-full rounded-lg border border-stone-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20" />
      </div>
      <button data-testid="compare-btn" disabled={!a || !b || loading} onClick={() => run()}
        className="mt-3 flex h-12 items-center gap-2 rounded-lg bg-navy px-5 text-sm font-medium text-white transition-colors hover:bg-navyd disabled:opacity-60">
        <Sparkles size={16} strokeWidth={1.5} /> Compare
      </button>

      <div className="mt-4 flex flex-wrap gap-2">
        {EXAMPLES.map(([x, y], i) => (
          <button key={i} data-testid={`compare-example-${i}`} onClick={() => run(x, y)}
            className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-50">
            {x} vs {y}
          </button>
        ))}
      </div>

      {loading && (
        <div className="mt-6 flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-6" data-testid="compare-loading">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-stone-300 border-t-navy" />
          <p className="text-sm text-stone-600">Comparing with GPT-5.5…</p>
        </div>
      )}

      {res && !loading && (
        <div className="mt-6 space-y-5 animate-in-up" data-testid="compare-report">
          <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <TriangleAlert size={18} className="mt-0.5 shrink-0 text-amber-700" strokeWidth={1.5} />
            <p className="text-sm text-amber-800">{res.warning}</p>
          </div>

          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <div className="grid grid-cols-[1.2fr_1fr_1fr] border-b border-stone-200 bg-stone-50 text-sm font-semibold text-stone-900">
              <div className="p-4">Metric</div>
              <div className="p-4">{res.company_a?.name} <span className="block text-xs font-normal text-stone-500">{res.company_a?.country} · {res.company_a?.currency}</span></div>
              <div className="p-4">{res.company_b?.name} <span className="block text-xs font-normal text-stone-500">{res.company_b?.country} · {res.company_b?.currency}</span></div>
            </div>
            {(res.metrics || []).map((m, i) => (
              <div key={m.metric || i} className="grid grid-cols-[1.2fr_1fr_1fr] border-b border-stone-100 text-sm last:border-0" data-testid={`compare-metric-${i}`}>
                <div className="p-4 font-medium text-stone-800">{m.metric}</div>
                <div className={`p-4 ${winnerCls(m.winner, "a")}`}>{m.a}</div>
                <div className={`p-4 ${winnerCls(m.winner, "b")}`}>{m.b}</div>
              </div>
            ))}
          </div>

          {res.ai_summary && (
            <div className="rounded-xl bg-stone-900 p-5 text-sm text-stone-100">
              <span className="font-semibold text-emerald-300">AI verdict: </span>{res.ai_summary}
            </div>
          )}
          <p className="text-center text-xs text-stone-400">{res.disclaimer}</p>
        </div>
      )}
    </main>
  );
}
